import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { resolveImageUrl } from "@/lib/resolveImageUrl";
import { toast } from "sonner";
import { Package, Plus, Search, Pencil, Trash2, Upload, Camera, X, ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 20;

const initialFormData = {
  name: "",
  code: "",
  published: false,
  description: "",
  price: "",
  unit: "unidad",
  category: "General",
  image_url: "",
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [togglingPublished, setTogglingPublished] = useState(null);
  const fileInputRef = useRef(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const [productsRes, countRes] = await Promise.all([
        axios.get(`${API}/admin/products`, {
          params: { skip: page * PAGE_SIZE, limit: PAGE_SIZE, search }
        }),
        axios.get(`${API}/admin/products/count`, { params: { search } })
      ]);
      setProducts(productsRes.data);
      setTotalCount(countRes.data.count);
    } catch (error) {
      toast.error("Error al cargar los productos");
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditId(product.product_id);
      setFormData({
        name: product.name,
        code: product.code || "",
        published: !!product.published,
        description: product.description || "",
        price: product.price.toString(),
        unit: product.unit,
        category: product.category,
        image_url: product.image_url || "",
      });
      setPreviewImage(resolveImageUrl(product.image_url || ""));
    } else {
      setEditId(null);
      setFormData(initialFormData);
      setPreviewImage("");
    }
    setDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Preview
    const reader = new FileReader();
    reader.onload = (e) => setPreviewImage(e.target.result);
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    const uploadData = new FormData();
    uploadData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload/image`, uploadData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      // Build full URL for the image using backend URL
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const fullUrl = `${baseUrl}${response.data.url}`;
      
      setFormData(prev => ({ ...prev, image_url: fullUrl }));
      setPreviewImage(fullUrl);
      toast.success("Foto subida correctamente");
    } catch (error) {
      toast.error("Error al subir la foto");
      setPreviewImage(formData.image_url);
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => {
    setFormData(prev => ({ ...prev, image_url: "" }));
    setPreviewImage("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const payload = {
      ...formData,
      price: parseFloat(formData.price),
      published: !!formData.published,
    };

    try {
      if (editId) {
        await axios.put(`${API}/admin/products/${editId}`, payload);
        toast.success("Producto actualizado");
      } else {
        await axios.post(`${API}/admin/products`, payload);
        toast.success("Producto creado");
      }
      setDialogOpen(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar el producto");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/products/${deleteId}`);
      toast.success("Producto eliminado");
      fetchProducts();
    } catch (error) {
      toast.error("Error al eliminar el producto");
    } finally {
      setDeleteId(null);
    }
  };

  const togglePublished = async (product) => {
    setTogglingPublished(product.product_id);
    try {
      const res = await axios.patch(`${API}/admin/products/${product.product_id}/published`);
      setProducts((prev) =>
        prev.map((p) =>
          p.product_id === product.product_id ? { ...p, published: res.data.published } : p
        )
      );
      toast.success(res.data.published ? "Producto publicado" : "Producto ocultado");
    } catch (error) {
      toast.error("Error al cambiar estado");
    } finally {
      setTogglingPublished(null);
    }
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

 

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-products">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Productos</h1>
              <p className="text-slate-500">{totalCount} productos en catálogo</p>
            </div>
          </div>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-blue-600 hover:bg-blue-700 gap-2"
            data-testid="add-product-btn"
          >
            <Plus className="w-4 h-4" />
            Nuevo Producto
          </Button>
        </div>

        {/* Search */}
        <Card className="bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre o categoría..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10"
                data-testid="search-products-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Products table */}
        <Card className="bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Catálogo de Productos</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">
                  {search ? "No se encontraron productos" : "No hay productos. Crea el primero."}
                </p>
              </div>
            ) : (
              <>
                {/* Mobile view - Cards */}
                <div className="md:hidden space-y-3">
                  {products.map((product) => (
                    <div
                      key={product.product_id}
                      className="border rounded-lg p-4 bg-white hover:bg-slate-50 transition-colors"
                      data-testid={`product-card-${product.product_id}`}
                    >
                      <div className="flex items-start gap-3">
                        {product.image_url ? (
                          <img
                            src={resolveImageUrl(product.image_url)}
                            alt={product.name}
                            className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Package className="w-6 h-6 text-slate-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">

                          {product.code && (
                            <p className="text-xs text-slate-500 font-mono mt-1">
                              {product.code}
                            </p>
                          )}

                          <p className="font-medium text-slate-900">{product.name}</p>
                          {product.description && (
                            <p className="text-sm text-slate-500 truncate">
                              {product.description}
                            </p>
                          )}
                          <p className="text-sm font-semibold text-blue-600 mt-1">
                            {product.price.toFixed(2)}€ / {product.unit}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9"
                              onClick={() => handleOpenDialog(product)}
                              data-testid={`edit-product-mobile-${product.product_id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => setDeleteId(product.product_id)}
                              data-testid={`delete-product-mobile-${product.product_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop view - Table */}
                <div className="hidden md:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Código</TableHead>
                        <TableHead>Producto</TableHead>
                        <TableHead>Categoría</TableHead>
                        <TableHead>Precio base</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {products.map((product) => (
                        <TableRow
                          key={product.product_id}
                          className="table-row-hover cursor-pointer"
                          data-testid={`product-row-${product.product_id}`}
                          onClick={() => handleOpenDialog(product)}
                        >
                          <TableCell>
                            <span className="text-sm font-mono text-slate-600">
                              {product.code || '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {product.image_url ? (
                                <img
                                  src={resolveImageUrl(product.image_url)}
                                  alt={product.name}
                                  className="w-10 h-10 rounded-lg object-cover"
                                  loading="lazy"
                                  decoding="async"
                                />
                              ) : (
                                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                  <Package className="w-5 h-5 text-slate-400" />
                                </div>
                              )}
                              <div>
                                <p className="font-medium text-slate-900">{product.name}</p>
                                {product.description && (
                                  <p className="text-sm text-slate-500 truncate max-w-[200px]">
                                    {product.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="px-2 py-1 bg-slate-100 rounded-md text-sm">
                              {product.category}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div>
                              <span className="font-semibold">{product.price.toFixed(2)}€</span>
                              <span className="text-slate-500 text-sm ml-1">/ {product.unit}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleOpenDialog(product)}
                                data-testid={`edit-product-${product.product_id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                                onClick={(e) => { e.stopPropagation(); setDeleteId(product.product_id); }}
                                data-testid={`delete-product-${product.product_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalCount > PAGE_SIZE && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Mostrando {page * PAGE_SIZE + 1} - {Math.min((page + 1) * PAGE_SIZE, totalCount)} de {totalCount} productos
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page - 1)}
                disabled={page === 0}
              >
                <ChevronLeft className="w-4 h-4" />
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={(page + 1) * PAGE_SIZE >= totalCount}
              >
                Siguiente
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Product form dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editId ? "Editar Producto" : "Nuevo Producto"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Cafetera Industrial"
                  required
                  data-testid="product-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="code">Código A3 / ERP</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="Ej: ART-001"
                  data-testid="product-code-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripción del producto..."
                  rows={2}
                  data-testid="product-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price">Precio (€) *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="product-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unit">Unidad *</Label>
                  <Input
                    id="unit"
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    placeholder="unidad, kg, litro..."
                    required
                    data-testid="product-unit-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Categoría</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="Ej: Maquinaria, Menaje..."
                  data-testid="product-category-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Foto del producto</Label>
                
                {/* Image preview */}
                {previewImage ? (
                  <div className="relative w-full h-40 bg-slate-100 rounded-lg overflow-hidden">
                    <img 
                      src={previewImage} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                      decoding="async"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 h-8 w-8"
                      onClick={clearImage}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                    {uploading && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <div className="spinner" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div 
                    className="w-full h-40 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Camera className="w-10 h-10 text-slate-400 mb-2" />
                    <p className="text-sm text-slate-500 font-medium">Toca para subir foto</p>
                    <p className="text-xs text-slate-400">JPG, PNG, WebP</p>
                  </div>
                )}
                
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={handleImageUpload}
                  data-testid="product-image-file-input"
                />
                
                {/* Button to change photo */}
                {previewImage && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full gap-2"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    <Upload className="w-4 h-4" />
                    Cambiar foto
                  </Button>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={submitting}
                  data-testid="save-product-btn"
                >
                  {submitting ? <div className="spinner" /> : "Guardar"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar producto?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer. Se eliminarán también las
                asignaciones a clientes de este producto.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-product-btn"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
